// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import { useLayoutEffect, useRef, useState } from 'react';
import { useResizeObserver } from '@cloudscape-design/component-toolkit/internal';
/**
 * Measures token and container widths to determine how many tokens can be shown
 * inline before the rest must collapse into the overflow "+N" pill.
 *
 * @param measureRef - ref to the invisible measurement container
 * @param triggerRowRef - ref to the visible token trigger row (for padding/gap CSS)
 * @param tokenCount - current total token count; changing it re-triggers measurement
 * @param enabled - set to false in non-token mode to skip all measurement work
 */
export function useTokenOverflow(measureRef, triggerRowRef, tokenCount, enabled) {
    const containerRef = useRef(null);
    const [visibleCount, setVisibleCount] = useState(undefined);
    const [tokenListMaxWidth, setTokenListMaxWidth] = useState(undefined);
    const lastOverflowResultRef = useRef({
        count: undefined,
        maxWidth: undefined,
    });
    const [containerResizeCount, setContainerResizeCount] = useState(0);
    const lastContainerWidthRef = useRef(0);
    useResizeObserver(() => (enabled ? containerRef.current : null), entry => {
        if (entry.borderBoxWidth > 0 && entry.borderBoxWidth !== lastContainerWidthRef.current) {
            lastContainerWidthRef.current = entry.borderBoxWidth;
            setContainerResizeCount(n => n + 1);
        }
    });
    useLayoutEffect(() => {
        var _a, _b;
        if (!enabled || !measureRef.current || !containerRef.current || !triggerRowRef.current) {
            return;
        }
        measureRef.current.inert = true;
        const containerWidth = (_a = containerRef.current.offsetWidth) !== null && _a !== void 0 ? _a : 0;
        if (containerWidth <= 0) {
            return;
        }
        const tokenEls = Array.from(measureRef.current.querySelectorAll('[data-measure-token]'));
        if (tokenEls.length === 0) {
            if (lastOverflowResultRef.current.count !== 0) {
                lastOverflowResultRef.current = { count: 0, maxWidth: undefined };
                setVisibleCount(0);
                setTokenListMaxWidth(undefined);
            }
            return;
        }
        const pillEl = measureRef.current.querySelector('[data-measure-pill]');
        const pillWidth = (_b = pillEl === null || pillEl === void 0 ? void 0 : pillEl.offsetWidth) !== null && _b !== void 0 ? _b : 0;
        const triggerEl = triggerRowRef.current;
        const triggerStyle = getComputedStyle(triggerEl);
        const paddingInline = parseFloat(triggerStyle.paddingInlineStart) + parseFloat(triggerStyle.paddingInlineEnd);
        const gap = parseFloat(triggerStyle.gap) || parseFloat(triggerStyle.columnGap);
        const iconEl = triggerEl.querySelector('[data-search-icon]');
        const iconWidth = iconEl.offsetWidth;
        const inputMinWidth = Math.floor(containerWidth * 0.2);
        const tokenWidths = tokenEls.map(el => el.offsetWidth);
        const totalAll = tokenWidths.reduce((s, w) => s + w, 0) + gap * Math.max(tokenWidths.length - 1, 0);
        const budget = containerWidth - paddingInline - iconWidth - inputMinWidth - pillWidth - gap - gap;
        let newCount;
        let newMaxWidth;
        if (totalAll <= budget) {
            newCount = tokenCount;
            newMaxWidth = undefined;
        }
        else {
            let used = 0;
            let count = 0;
            for (let i = tokenWidths.length - 1; i >= 0; i--) {
                if (used + gap + tokenWidths[i] > budget) {
                    break;
                }
                used += gap + tokenWidths[i];
                count++;
            }
            newCount = count;
            newMaxWidth = budget + gap + pillWidth;
        }
        if (newCount !== lastOverflowResultRef.current.count || newMaxWidth !== lastOverflowResultRef.current.maxWidth) {
            lastOverflowResultRef.current = { count: newCount, maxWidth: newMaxWidth };
            setVisibleCount(newCount);
            setTokenListMaxWidth(newMaxWidth);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, tokenCount, containerResizeCount]);
    return { containerRef, visibleCount, tokenListMaxWidth };
}
//# sourceMappingURL=use-token-overflow.js.map